import { ref } from 'vue'
import { Auth, Device, Dict, DictItem, Env, ModuleConfig } from '@nio-pow/mfe-vue3'
import { permissionList } from '@/auth'
import { fetchBackend } from '@/utils/fetch'
import emiter from '@/utils/emiter'

export type HeaderOption = {
  title: string
  value: any
  [key: string]: any
}

// 查询当前页面路由对应字典表的那个选项
function getSelectedTitleOptionIndex(options: any[]) {
  return options.reduce((prev: number, curOption: any, index: number) => {
    const curPath = window.location.pathname
    const curOptionPath = encodeURI(curOption.value)
    if (curOptionPath.indexOf(curPath) === 0) prev = index
    return prev
  }, -1)
}

// 无关的兄弟节点低成本传参用
export const HeaderUtils = {
  equipmentListTitleOptions: [] as any[],

  EVENT_TITLE_OPTIONS: '__POWER-GO-MAIN_EVENT-TITLE-OPTION__',

  clearTitleOptions() {
    emiter.emit(this.EVENT_TITLE_OPTIONS, false)
  },
  setTitleOptions(options: HeaderOption[], defaultIndex: number, callback: any) {
    emiter.emit(this.EVENT_TITLE_OPTIONS, {
      options,
      defaultIndex,
      callback,
    })
  },
  onTitleOptionsChange(callback: (payload: any) => void) {
    emiter.on(this.EVENT_TITLE_OPTIONS, callback)
  },

  // 特殊业务场景，专门用于各种设备列表页（移动端）设置顶部下拉标题用
  setEquipmentListTitle() {
    this._setEquipmentListTitleSelect()

    // 更新下拉内容
    Dict.getDict('equipment_list_header_options').then((dict: any) => {
      this.equipmentListTitleOptions = (dict as DictItem[])
        .map((item: DictItem) => ({
          title: item.label,
          value: item.value,
          auth: item.desc,
        }))
        .filter((item: any) => Auth.hasPermission(item.auth))

      this._setEquipmentListTitleSelect()
    })
  },

  _setEquipmentListTitleSelect() {
    if (this.equipmentListTitleOptions.length) {
      const index = getSelectedTitleOptionIndex(this.equipmentListTitleOptions)
      this.setTitleOptions(this.equipmentListTitleOptions, index, (option: any) => {
        window.history.replaceState({}, '', option.value)
      })
    }
  },
}

// 是否显示V2版本PC顶部导航
export const showPCHeaderV2 = !Device.isMobile()
if (showPCHeaderV2) {
  document.documentElement.setAttribute('data-show-pc-header-v2', 'true')
} else {
  document.documentElement.setAttribute('data-show-pc-header-v2', 'false')
}

// 是否显示带返回按钮的顶部导航（飞书小程序内或移动端必定显示）
export const showHeaderNav = Device.isFeishu() || Device.isMobile()
if (showHeaderNav) {
  document.documentElement.setAttribute('data-show-header-nav', 'true')
} else {
  document.documentElement.setAttribute('data-show-header-nav', 'false')
}

// 综合情况下的顶部导航高度
export const headerHeight = (showHeaderNav ? 44 : 0) + (showPCHeaderV2 ? 50 : 0)

/**
 * 获取员工信息(头像)
 * https://apidoc.nioint.com/project/8896/interface/api/712009
 * @param user_id
 */
export function apiGetEmployeeInfo(user_id: string) {
  const url = '/pe/powerpro/powerpro-personal/employees'
  return fetchBackend.post(url, { params: { user_id, use_cache: true } })
}

// 设置环境提示全局样式
function _setEnvHintGlobalStyle(
  envHintBgColor: string | undefined,
  envHintTagColor: string | undefined,
) {
  const id = 'headerNavGlobalStyle'
  let styleDom = document.querySelector(`#${id}`)
  if (!styleDom) {
    styleDom = document.createElement('style')
    styleDom.id = id
    document.head.appendChild(styleDom)
  }

  styleDom.innerHTML = `
      body {
        --env-hint-bg-color: ${envHintBgColor};
        --env-hint-tag-color: ${envHintTagColor};
      }
    `
}

// 是否显示当前所处环境提示
export function useEnvHint() {
  // 是否显示当前所处环境提示
  const env = Env.getEnv()
  const isProd = env === 'prod'

  // 初始化默认值
  const envHintText = ref(isProd ? '生产环境' : env.toUpperCase())
  const envHintBgColor = ref(isProd ? 'rgb(255, 228, 186)' : undefined)
  const envHintTagColor = ref(isProd ? '#f53f3f' : undefined)
  const showEnvHint = ref(Auth.hasPermission(permissionList.POWERPRO_SHOW_ENV_HINT))
  _setEnvHintGlobalStyle(envHintBgColor.value, envHintTagColor.value)

  // 尝试读取远程配置
  const configId = 'powergo_global_config'
  ModuleConfig.getConfig([configId]).then((configs: any) => {
    const config = configs[configId]?.configBody || {}
    envHintText.value = config.envHint?.text || envHintText.value
    envHintBgColor.value = config.envHint?.bgColor || envHintBgColor.value
    envHintTagColor.value = config.envHint?.tagColor || envHintTagColor.value
    _setEnvHintGlobalStyle(envHintBgColor.value, envHintTagColor.value)
  })

  return {
    envHintBgColor,
    envHintTagColor,
    envHintText,
    showEnvHint,
  }
}
